import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Upload, X, ImagePlus, Check } from 'lucide-react';

interface BulkCameraSessionProps {
  onFilesReady: (files: File[]) => void;
  maxProducts: number;
  onBack: () => void;
  autoStart?: boolean;
  singleMode?: boolean;
}

export const BulkCameraSession: React.FC<BulkCameraSessionProps> = ({
  onFilesReady,
  maxProducts,
  onBack,
  autoStart,
  singleMode,
}) => {
  // All captured/selected files are held locally until "Done"
  const [capturedFiles, setCapturedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);

  // Camera refs
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  const desktopVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reachedLimit = capturedFiles.length >= maxProducts;

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    [mobileVideoRef, desktopVideoRef].forEach(ref => {
      if (ref.current) ref.current.srcObject = null;
    });
    setCameraActive(false);
  }, []);

  const closeCamera = useCallback(() => {
    stopCamera();
    onBack();
  }, [stopCamera, onBack]);

  // Attach stream to both video elements
  const attachStream = useCallback(() => {
    if (!streamRef.current) return;
    [mobileVideoRef, desktopVideoRef].forEach(ref => {
      if (ref.current && !ref.current.srcObject) {
        ref.current.srcObject = streamRef.current;
        ref.current.play().catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    if (autoStart) startCamera();
    if (singleMode) setShowModal(true);
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for video element mount and attach stream when ready
  useEffect(() => {
    if (cameraActive) attachStream();
  }, [cameraActive, attachStream]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => previews.forEach(url => URL.revokeObjectURL(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback((files: FileList | File[]) => {
    const remaining = maxProducts - capturedFiles.length;
    const filesToAdd = Array.from(files).slice(0, remaining);
    if (filesToAdd.length === 0) return;

    setCapturedFiles(prev => [...prev, ...filesToAdd]);
    const newPreviews = filesToAdd.map(f => URL.createObjectURL(f));
    setPreviews(prev => [...prev, ...newPreviews]);
  }, [capturedFiles.length, maxProducts]);

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        onBack();
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      // Attach immediately if video elements are already rendered
      [mobileVideoRef, desktopVideoRef].forEach(ref => {
        if (ref.current) ref.current.srcObject = stream;
      });
      setCameraActive(true);
      setShowModal(false);
    } catch (error) {
      console.error('Error accessing camera:', error);
      onBack();
    }
  };

  const capturePhoto = () => {
    const video = mobileVideoRef.current || desktopVideoRef.current;
    if (!video || !canvasRef.current || reachedLimit) return;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        if (singleMode) {
          stopCamera();
          onFilesReady([file]);
          return;
        }
        setCapturedFiles(prev => [...prev, file]);
        setPreviews(prev => [...prev, URL.createObjectURL(file)]);
      }
    }, 'image/jpeg', 0.8);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (singleMode) {
        const file = e.target.files[0];
        stopCamera();
        onFilesReady([file]);
        e.target.value = '';
        return;
      }
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const openGallery = () => {
    setShowModal(false);
    fileInputRef.current?.click();
  };

  const handleDone = () => {
    if (capturedFiles.length === 0) return;
    stopCamera();
    onFilesReady(capturedFiles);
  };

  const removeFile = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setCapturedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="relative h-full animate-in fade-in duration-300">
      {cameraActive ? (<>
        {/* ===== MOBILE: Full-screen camera modal (default) ===== */}
        <div className="fixed inset-0 z-[500] bg-black lg:hidden">
          <video ref={mobileVideoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/10 pointer-events-none" />
          <canvas ref={canvasRef} className="hidden" />
          <div id="flash-overlay" className="absolute inset-0 bg-white opacity-0 pointer-events-none transition-opacity duration-150" />

          {/* Top overlay */}
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/60 to-transparent px-5 pt-12 pb-8">
            <div className="flex items-center justify-between">
              <button onClick={onBack} className="w-9 h-9 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60 transition-colors">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg>
              </button>
              {!singleMode && (
                <div className="flex items-center gap-3">
                  <span className="text-white font-semibold text-sm drop-shadow-lg">{capturedFiles.length}/{maxProducts}</span>
                  {capturedFiles.length > 0 && (
                    <button onClick={handleDone} className="px-4 py-1.5 bg-emerald-500/90 hover:bg-emerald-500 text-white text-sm font-bold rounded-full transition-colors flex items-center gap-1">
                      <Check className="w-4 h-4" /> Done
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Bottom overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pt-16 pb-6 px-5">
            {capturedFiles.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {previews.map((preview, i) => (
                    <div key={i} className="relative shrink-0 group">
                      <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-white/60">
                        <img src={preview} alt="" className="w-full h-full object-cover" />
                      </div>
                      <button onClick={() => removeFile(i)} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shadow-lg">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {!reachedLimit && (
                    <button onClick={() => setShowModal(true)} className="w-12 h-12 rounded-lg border-2 border-dashed border-white/40 flex items-center justify-center hover:border-white/70 transition-colors shrink-0">
                      <ImagePlus className="w-5 h-5 text-white/70" />
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center justify-center gap-6">
              <button onClick={openGallery} className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60 transition-colors">
                <ImagePlus className="w-5 h-5 text-white" />
              </button>
              <button onClick={capturePhoto} disabled={reachedLimit} className="w-16 h-16 rounded-full border-4 border-white bg-white/20 hover:bg-white/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
                <div className="w-12 h-12 rounded-full bg-white mx-auto" />
              </button>
              <button onClick={closeCamera} className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60 transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* ===== DESKTOP: Contained camera ===== */}
        <div className="hidden lg:flex flex-col h-full gap-4">
          <div className="flex items-center justify-between shrink-0">
            <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg> Back
            </button>
            {!singleMode && (
              <>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{capturedFiles.length}/{maxProducts} captured</span>
                {capturedFiles.length > 0 && (
                  <button onClick={handleDone} className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-1.5">
                    <Check className="w-4 h-4" /> Done
                  </button>
                )}
              </>
            )}
          </div>

          <div className="flex-1 flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-[4/3] bg-black rounded-2xl overflow-hidden">
              <video ref={desktopVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-6">
                <button onClick={openGallery} className="w-11 h-11 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60 transition-colors">
                  <ImagePlus className="w-5 h-5 text-white" />
                </button>
                <button onClick={capturePhoto} disabled={reachedLimit} className="w-16 h-16 rounded-full border-4 border-white bg-white/20 hover:bg-white/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
                  <div className="w-12 h-12 rounded-full bg-white mx-auto" />
                </button>
                <div className="w-11 h-11" />
              </div>
              <button onClick={closeCamera} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors">
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {capturedFiles.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-3 border border-gray-200 dark:border-gray-800 shrink-0">
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {previews.map((preview, i) => (
                  <div key={i} className="relative shrink-0 group">
                    <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-700">
                      <img src={preview} alt="" className="w-full h-full object-cover" />
                    </div>
                    <button onClick={() => removeFile(i)} className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {!reachedLimit && (
                  <button onClick={() => setShowModal(true)} className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors shrink-0">
                    <Camera className="w-5 h-5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </>) : null}

      {/* Hidden gallery input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        multiple={!singleMode}
        onChange={handleFileSelect}
      />

      {/* Picker Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-[500] flex items-end lg:items-center justify-center">
          <div className="bg-white dark:bg-black rounded-t-2xl lg:rounded-2xl w-full max-w-md lg:max-w-sm animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{singleMode ? 'Add Photo' : 'Add Photos'}</h3>
              <button onClick={() => { if (singleMode) onBack(); else setShowModal(false); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-900 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-2">
              <button onClick={startCamera} className="flex items-center w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-900 rounded-lg transition-colors">
                <Camera className="w-5 h-5 text-blue-600 mr-3" />
                <span className="text-gray-900 dark:text-white font-medium">Take Photo</span>
              </button>
              <button onClick={openGallery} className="flex items-center w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-900 rounded-lg transition-colors">
                <Upload className="w-5 h-5 text-blue-600 mr-3" />
                <span className="text-gray-900 dark:text-white font-medium">Choose from Gallery</span>
              </button>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-800">
              <button onClick={() => { if (singleMode) onBack(); else setShowModal(false); }} className="w-full py-3 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-900 rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
